import { useState, useEffect } from 'react';
import { apiGet, apiPost } from '@/lib/api-client';

interface APIKey {
  id: string;
  provider: 'openai' | 'anthropic' | 'google';
  keyName: string;
  isActive: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

export function APIKeyManager() {
  const [keys, setKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newKey, setNewKey] = useState({
    provider: 'openai' as const,
    keyName: '',
    apiKey: '',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadAPIKeys();
  }, []);

  async function loadAPIKeys() {
    try {
      setLoading(true);

      const data = await apiGet<APIKey[]>('/api/admin/api-keys');
      setKeys(data);
    } catch (err) {
      console.error('Error loading API keys:', err);
      setMessage({ type: 'error', text: 'Failed to load API keys' });
    } finally {
      setLoading(false);
    }
  }

  async function handleAddKey() {
    try {
      setSaving(true);
      setMessage(null);

      if (!newKey.keyName || !newKey.apiKey) {
        setMessage({ type: 'error', text: 'Please fill in all fields' });
        return;
      }

      await apiPost('/api/admin/api-keys', {
        provider: newKey.provider,
        keyName: newKey.keyName,
        apiKey: newKey.apiKey,
      });

      setMessage({ type: 'success', text: 'API key added successfully!' });
      setShowAddForm(false);
      setNewKey({ provider: 'openai', keyName: '', apiKey: '' });
      await loadAPIKeys();
    } catch (err: any) {
      console.error('Error adding API key:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to add API key' });
    } finally {
      setSaving(false);
    }
  }

  async function handleTestKey(provider: string) {
    try {
      const data = await apiPost<{ success: boolean; error?: string }>('/api/admin/api-keys/test', {
        provider,
      });

      if (data.success) {
        setMessage({ type: 'success', text: `${provider} API key is valid` });
      } else {
        setMessage({ type: 'error', text: data.error || `${provider} API key test failed` });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Key test failed' });
    }
  }

  const providerIcons = {
    openai: <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />,
    anthropic: <span className="w-2 h-2 rounded-full bg-purple-500 inline-block" />,
    google: <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />,
  };

  const providerNames = {
    openai: 'OpenAI',
    anthropic: 'Anthropic',
    google: 'Google Gemini',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white mb-2">API Key Management</h2>
          <p className="text-slate-400 text-sm">Securely manage LLM provider API keys</p>
        </div>

        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-royal-gradient text-white font-medium rounded-lg
                   hover:opacity-90 transition-opacity shadow-glow flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add New Key
        </button>
      </div>

      {message && (
        <div
          className={`p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
              : 'bg-red-500/10 border border-red-500/30 text-red-400'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Add New Key Form */}
      {showAddForm && (
        <div className="glass-strong p-6 rounded-xl border-2 border-amber-500/30">
          <h3 className="text-lg font-semibold text-white mb-4">Add New API Key</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Provider</label>
              <select
                value={newKey.provider}
                onChange={(e) => setNewKey({ ...newKey, provider: e.target.value as typeof newKey.provider })}
                className="w-full px-4 py-2 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white
                         focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none"
              >
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
                <option value="google">Google Gemini</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Key Name</label>
              <input
                type="text"
                value={newKey.keyName}
                onChange={(e) => setNewKey({ ...newKey, keyName: e.target.value })}
                placeholder="e.g., production-key"
                className="w-full px-4 py-2 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white
                         focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none
                         placeholder:text-slate-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">API Key</label>
              <input
                type="password"
                value={newKey.apiKey}
                onChange={(e) => setNewKey({ ...newKey, apiKey: e.target.value })}
                placeholder="sk-..."
                className="w-full px-4 py-2 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white font-mono
                         focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none
                         placeholder:text-slate-600"
              />
              <p className="text-xs text-slate-500 mt-1">
                Keys are encrypted before storage
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleAddKey}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg
                         transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Adding...' : 'Add Key'}
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewKey({ provider: 'openai', keyName: '', apiKey: '' });
                }}
                className="px-4 py-2 bg-charcoal-700 hover:bg-charcoal-600 text-white font-medium rounded-lg
                         transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Existing Keys List */}
      <div className="space-y-3">
        {keys.length === 0 ? (
          <div className="glass-strong p-8 rounded-xl text-center">
            <svg
              className="w-16 h-16 mx-auto mb-4 text-slate-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
              />
            </svg>
            <p className="text-slate-400">No API keys configured</p>
            <p className="text-sm text-slate-500 mt-1">Add your first API key to get started</p>
          </div>
        ) : (
          keys.map((key) => (
            <div key={key.id} className="glass-strong p-4 rounded-lg hover:bg-charcoal-800/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {providerIcons[key.provider]}
                  <div>
                    <h4 className="font-medium text-white">{providerNames[key.provider]}</h4>
                    <p className="text-sm text-slate-400">{key.keyName}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {key.isActive && (
                    <span className="px-2 py-1 text-xs font-medium bg-emerald-500/20 text-emerald-400 rounded">
                      Active
                    </span>
                  )}

                  {key.lastUsedAt && (
                    <span className="text-xs text-slate-500">
                      Last used: {new Date(key.lastUsedAt).toLocaleDateString()}
                    </span>
                  )}

                  <button
                    onClick={() => handleTestKey(key.provider)}
                    className="px-3 py-1.5 text-sm bg-charcoal-700 hover:bg-charcoal-600 text-white rounded-lg
                             transition-colors"
                  >
                    Test
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Security Notice */}
      <div className="glass p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
        <div className="flex gap-3">
          <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-amber-400 mb-1">Security Best Practices</h4>
            <ul className="text-xs text-slate-400 space-y-1">
              <li>* API keys are encrypted using AES-256 before storage</li>
              <li>* Keys are never exposed to the frontend</li>
              <li>* Only super admins can view, add, or modify keys</li>
              <li>* All key operations are logged in the audit trail</li>
              <li>* Rotate keys regularly (recommended: every 90 days)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
